import { ReactNode, useCallback, useMemo, useState } from "react";
import { GlobalStyles, Theme, useTheme } from "@mui/material";
import { JUMP_MARKS_BAR_HEIGHT_PX } from "src/components-mui/common/jump-marks/jump-marks-bar";
import { createJumpMarksState, createJumpMarksStore, JumpMarksContext } from "src/components-mui/common/jump-marks/jump-marks.context";
import { IJumpMarkItem, IJumpMarkSectionRegistration } from "src/components-mui/common/jump-marks/jump-marks.types";
import { getSectionScrollMarginPx } from "src/components-mui/common/jump-marks/jump-marks.utils";
import { useActiveSectionTracking } from "src/components-mui/common/jump-marks/use-active-section-tracking";
import { useKeepUrlAnchorInView } from "src/components-mui/common/jump-marks/use-keep-url-anchor-in-view";
import { useSectionNavigation } from "src/components-mui/common/jump-marks/use-section-navigation";
import { TResponsivePx } from "src/types/responsive";
import { resolveResponsivePx } from "src/utils/mui/responsive-px/responsive-px";

interface IJumpMarksProviderProps {
    items: readonly IJumpMarkItem[];
    stickyNavHeightPx: TResponsivePx;
    barHeightPx?: TResponsivePx;
    children: ReactNode;
}

const getScrollPaddingTopStyles = (theme: Theme, scrollMarginPx: TResponsivePx) => {
    const breakpoints = theme.breakpoints.keys;
    const resolvedScrollMarginPx = resolveResponsivePx({ valuesPx: scrollMarginPx, breakpoints });

    return {
        html: breakpoints.reduce(
            (styles, breakpoint, index) =>
                index > 0 && resolvedScrollMarginPx[breakpoint] === resolvedScrollMarginPx[breakpoints[index - 1]]
                    ? styles
                    : { ...styles, [theme.breakpoints.up(breakpoint)]: { scrollPaddingTop: `${resolvedScrollMarginPx[breakpoint]}px` } },
            {}
        ),
    };
};

const warnAboutSecondAnchor = (anchors: ReadonlyMap<string, HTMLElement>, { id, element }: IJumpMarkSectionRegistration) => {
    const registeredAnchor = anchors.get(id);

    if (process.env.NODE_ENV === "production" || !registeredAnchor || registeredAnchor === element) {
        return;
    }

    // eslint-disable-next-line no-console
    console.warn(`JumpMarkSection "${id}" has more than one anchor; tabs and #${id} links only scroll to the latest one.`);
};

export const JumpMarksProvider = ({ items, stickyNavHeightPx, barHeightPx = JUMP_MARKS_BAR_HEIGHT_PX, children }: IJumpMarksProviderProps) => {
    const theme = useTheme();
    const [state] = useState(() => createJumpMarksState({ visibleId: items[0]?.id ?? "" }));
    const [store] = useState(() => createJumpMarksStore(state));
    const [anchors] = useState(() => new Map<string, HTMLElement>());

    const scrollMarginPx = useMemo(
        () => getSectionScrollMarginPx({ stickyNavHeightPx, barHeightPx, breakpoints: theme.breakpoints.keys }),
        [barHeightPx, stickyNavHeightPx, theme]
    );
    const scrollPaddingTopStyles = useMemo(() => getScrollPaddingTopStyles(theme, scrollMarginPx), [scrollMarginPx, theme]);

    const trackSection = useActiveSectionTracking({ items, state, scrollMarginPx });
    const { navigateToSection, scrollToSection } = useSectionNavigation({ items, state, anchors });
    useKeepUrlAnchorInView({ anchors, state });

    const registerSection = useCallback(
        (registration: IJumpMarkSectionRegistration) => {
            const { id, element, isAnchor } = registration;
            const untrackSection = trackSection({ id, element });

            if (isAnchor) {
                warnAboutSecondAnchor(anchors, registration);
                anchors.set(id, element);
            }

            return () => {
                untrackSection();

                if (anchors.get(id) === element) {
                    anchors.delete(id);
                }
            };
        },
        [anchors, trackSection]
    );

    const contextValue = useMemo(
        () => ({ items, store, stickyTopPx: stickyNavHeightPx, registerSection, navigateToSection, scrollToSection }),
        [items, navigateToSection, registerSection, scrollToSection, stickyNavHeightPx, store]
    );

    return (
        <JumpMarksContext.Provider value={contextValue}>
            <GlobalStyles styles={scrollPaddingTopStyles} />
            {children}
        </JumpMarksContext.Provider>
    );
};
